<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { ChevronRight, Pencil, ShieldAlert } from 'lucide-svelte';
  import { docsApi } from '$lib/api/docs';
  import { getErrorMessage } from '$lib/api/client';
  import { route } from '$lib/state/route.svelte';
  import { session } from '$lib/state/session.svelte';
  import { relTime } from '$lib/utils/format';
  import Chip from '$lib/components/ui/Chip.svelte';
  import EmptyState from '$lib/components/ui/EmptyState.svelte';
  import DocModal from '$lib/components/workspace/DocModal.svelte';

  const { docId }: { docId: number } = $props();
  const admin = $derived(session.isAdmin);

  const docQuery = createQuery(() => ({
    queryKey: ['doc', docId],
    queryFn: () => docsApi.get(docId)
  }));
  const doc = $derived(docQuery.data);
  const accessible = $derived(!doc || !!session.me?.projects.some((p) => p.id === doc.project_id));

  // Markdown-lite parse: `# ` prefixed lines become h3 (prefix stripped);
  // every other line — including blank ones, for spacing — is its own div.
  const bodyLines = $derived(doc ? doc.body.split(/\n/) : []);

  let showEdit = $state(false);

  function goBack() {
    if (route.current.tab === 'doc' && route.current.back) route.push(route.current.back);
    else if (doc) route.push({ tab: 'project', projectId: doc.project_id });
    else route.push({ tab: 'overview' });
  }
</script>

{#if docQuery.isLoading}
  <p style="color:var(--t3);font-size:13.5px">Loading document…</p>
{:else if docQuery.isError}
  <p style="color:var(--danger);font-size:13.5px">{getErrorMessage(docQuery.error)}</p>
{:else if !accessible}
  <EmptyState icon={ShieldAlert} heading="No access" sub="This account hasn’t been granted access to this project." />
{:else if doc}
  <div style="max-width:780px;margin:0 auto">
    <div style="display:flex;align-items:center;margin-bottom:18px">
      <button type="button" class="btn btn-ghost btn-sm" style="margin-left:-6px" onclick={goBack}>
        <ChevronRight size={15} strokeWidth={2} />Back
      </button>
      <span style="flex:1"></span>
      {#if admin}
        <button type="button" class="btn btn-secondary btn-sm" onclick={() => (showEdit = true)}>
          <Pencil size={15} />Edit
        </button>
      {:else}
        <Chip label="Read-only" kind="gray" />
      {/if}
    </div>
    <h1 class="h-title" style="font-size:28px;margin-bottom:8px">{doc.title}</h1>
    <div style="font-size:12.5px;color:var(--t3);margin-bottom:26px">Updated {relTime(doc.updated_at)} · {doc.updated_by}</div>
    <div class="doc-body">
      {#each bodyLines as line, i (i)}
        {#if /^#\s/.test(line)}
          <h3>{line.replace(/^#\s/, '')}</h3>
        {:else}
          <div>{line}</div>
        {/if}
      {/each}
    </div>
  </div>
{/if}

{#if showEdit && doc}
  <DocModal projectId={doc.project_id} {doc} onClose={() => (showEdit = false)} />
{/if}
